import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, ArrowRight } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SpeakersGrid = () => {
  const [speakers, setSpeakers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/intervenants`).then(r => setSpeakers(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen pt-16">
      {/* PAGE HERO */}
      <section className="relative py-24 lg:py-32 bg-[#1E3A5F] overflow-hidden" data-testid="speakers-hero">
        <div className="relative container-institutional">
          <p className="text-sm font-semibold uppercase tracking-widest text-[#F5A623] mb-4">Intervenants</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-5 leading-tight">Nos Speakers</h1>
          <p className="text-lg text-white/70 max-w-2xl leading-relaxed">Découvrez les experts et leaders d'opinion qui interviendront lors du Forum URBANIK 2026.</p>
        </div>
      </section>

      {/* SPEAKERS GRID */}
      <section className="py-20 lg:py-28" data-testid="speakers-grid">
        <div className="container-institutional">
          {loading ? <p className="text-center text-[#94A3B8] py-12">Chargement...</p> : speakers.length === 0 ? (
            <p className="text-center text-[#94A3B8] py-12">Les intervenants seront annoncés prochainement.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {speakers.map(s => (
                <Link key={s.id} to={`/intervenants/${s.id}`} className="bg-white border border-[#E1E8F0] rounded-lg overflow-hidden hover:shadow-lg transition-all group" data-testid={`speaker-${s.id}`}>
                  <div className="overflow-hidden"><img src={s.photo_url} alt={s.name} className="w-full aspect-[4/5] object-cover group-hover:scale-105 transition-transform duration-300" /></div>
                  <div className="p-5">
                    <h3 className="font-bold text-[#1E3A5F] group-hover:text-[#F5A623] transition-colors">{s.name}</h3>
                    <p className="text-sm text-[#6B7C93] mt-1 leading-relaxed">{s.role}</p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-28 bg-[#1E3A5F]" data-testid="speakers-cta">
        <div className="container-institutional text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Venez les rencontrer</h2>
          <p className="text-white/70 max-w-xl mx-auto mb-8">Inscrivez-vous pour assister aux conférences et échanger avec nos speakers.</p>
          <Link to="/participer" className="btn-primary text-base px-8 py-4">S'inscrire <ArrowRight className="w-5 h-5 ml-2" /></Link>
        </div>
      </section>
    </div>
  );
};

const SpeakerProfile = () => {
  const { id } = useParams();
  const [speaker, setSpeaker] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [spRes, prRes] = await Promise.all([
          axios.get(`${API}/intervenants`),
          axios.get(`${API}/program`),
        ]);
        const found = spRes.data.find(s => s.id === id);
        setSpeaker(found || null);
        if (found) setSessions(prRes.data.filter(p => p.speaker && p.speaker.toLowerCase().includes(found.name.split(' ')[1]?.toLowerCase() || found.name.toLowerCase())));
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    load();
  }, [id]);

  if (loading) return <div className="min-h-screen pt-16 flex items-center justify-center"><p className="text-[#94A3B8]">Chargement...</p></div>;
  if (!speaker) return <div className="min-h-screen pt-16 flex items-center justify-center"><p className="text-[#94A3B8]">Intervenant non trouvé.</p></div>;

  return (
    <div className="min-h-screen pt-16">
      <section className="py-20 lg:py-28 bg-[#F7F9FC]" data-testid="speaker-profile">
        <div className="container-institutional">
          <Link to="/intervenants" className="inline-flex items-center gap-2 text-sm text-[#6B7C93] hover:text-[#1E3A5F] mb-8 transition-colors"><ArrowLeft className="w-4 h-4" /> Retour aux intervenants</Link>
          <div className="bg-white border border-[#E1E8F0] rounded-lg overflow-hidden">
            <div className="flex flex-col lg:flex-row">
              <div className="lg:w-80 flex-shrink-0"><img src={speaker.photo_url} alt={speaker.name} className="w-full lg:h-full object-cover aspect-[4/5] lg:aspect-auto" /></div>
              <div className="p-8 lg:p-12 flex-1">
                <h1 className="text-3xl font-bold text-[#1E3A5F] mb-2" data-testid="speaker-name">{speaker.name}</h1>
                <p className="text-[#F5A623] font-semibold mb-6">{speaker.role}</p>
                <p className="text-[#4A5568] leading-relaxed mb-8">Expert reconnu dans son domaine, {speaker.name} apportera son expertise et sa vision lors du Forum URBANIK 2026.</p>
                {sessions.length > 0 && (
                  <div>
                    <h3 className="text-sm font-bold text-[#1E3A5F] uppercase tracking-wider mb-4">Sessions</h3>
                    <div className="space-y-3">
                      {sessions.map(s => (
                        <div key={s.id} className="p-4 bg-[#F7F9FC] rounded-lg border border-[#E1E8F0]">
                          <p className="font-semibold text-sm text-[#1E3A5F]">{s.title}</p>
                          <p className="text-xs text-[#94A3B8] mt-1">{s.time} — {s.location}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export { SpeakersGrid, SpeakerProfile };
